import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import axios from "axios";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const Exams = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const courseID = courseId;
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [scores, setScores] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [newExam, setNewExam] = useState({ name: "", maxMarks: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExamData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/course/get-all-exam-ids-of-a-course`,
          { courseID: courseID },
          { withCredentials: true }
        );

        const exam = response.data.exams || [];
        setExams(exam);
        if (exam.length > 0) {
          // Fetch students from the first quiz
          const firstExam = exam[0].name;
          const getStudents = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/course/get-all-exam-scores-given-exam-id`,
            { examID: firstExam, courseID: courseID },
            { withCredentials: true }
          );

          const students = getStudents.data.scores.map(
            (student) => student.studentID
          );
          setStudents(students || []);

          // Fetch scores for all quizzes in parallel
          const examScoresResponses = await Promise.all(
            exam.map((exam) =>
              axios.post(
                `${process.env.REACT_APP_BASE_URL}/course/get-all-exam-scores-given-exam-id`,
                { examID: exam.name, courseID: courseID },
                { withCredentials: true }
              )
            )
          );

          // Process scores data and update state immutably
          let allScores = {};
          examScoresResponses.forEach((examResponse) => {
            console.log(examResponse.data);
            (examResponse.data.scores || []).forEach((item) => {
              const studentID = item.studentID;
              const quizName = item.score?.name;
              const marks = item.score?.marks;
              console.log(studentID, quizName, marks);
              if (!allScores[studentID]) {
                allScores[studentID] = {};
              }
              allScores[studentID][quizName] = marks;
            });
          });

          setScores(allScores);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching quiz data:", error);
        setLoading(false);
      }
    };

    if (courseID) {
      fetchExamData();
    }
  }, [courseID]);

  const handleAddExam = async () => {
    if (!newExam.name || !newExam.maxMarks) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/add-exam`,
        {
          courseID: courseID,
          examID: newExam.name,
          maxScore: parseInt(newExam.maxMarks),
        }, {withCredentials: true}
      );
      if (response.status === 200) {
        setExams([...exams, newExam]);
        setNewExam({ name: "", maxMarks: "" });
        setOpenDialog(false);
      }
    } catch (error) {
      console.error("Error adding exam:", error);
    }
  };

  const updateScore = (studentId, examName, score) => {
    try {
      const response = axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/edit-exam-scores`,
        {
          courseID: courseID,
          studentID: studentId,
          score: score,
          examID: examName,
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        console.log("Score updated successfully");
        setScores((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            [examName]: parseFloat(score),
          },
        }));
      }
    } catch (err) {
      console.log("Error updating score");
    }
  };

  const exportData = () => {
    return students.map((student) => ({
      StudentID: student,
      Name: student.name,
      ...exams.reduce(
        (acc, exam) => ({
          ...acc,
          [`Exam_${exam.name}`]: scores[student]?.[exam.name] || "",
        }),
        {}
      ),
    }));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h4" sx={{ color: "#FF9800" }}>
            {courseID} - Exams
            <Typography
              component="span"
              variant="h6"
              sx={{ ml: 2, color: "#666" }}
            >
              Section {courseID?.split("-")[2]} | {courseID?.split("-")[1]}
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpenDialog(true)}
            sx={{ backgroundColor: "#FF9800" }}
          >
            Add Exam
          </Button>
          <CSVLink data={exportData()} filename={`${courseID}_exams.csv`}>
            <Button
              startIcon={<FileDownloadIcon />}
              variant="contained"
              sx={{ backgroundColor: "#FF9800" }}
            >
              Export CSV
            </Button>
          </CSVLink>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
              >
                Student ID
              </TableCell>
              {exams.map((exam, index) => (
                <TableCell
                  key={index}
                  sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
                >
                  {exam.name} (Max: {exam.maxMarks})
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((student) => (
              <TableRow key={student} hover>
                <TableCell>{student}</TableCell>
                {exams.map((exam, index) => (
                  <TableCell key={index}>
                    <TextField
                      type="number"
                      size="small"
                      value={scores[student]?.[exam.name]}
                      onChange={(e) => {
                        // Update the scores state as the user types
                        const newScore = e.target.value;
                        setScores((prevScores) => ({
                          ...prevScores,
                          [student]: {
                            ...prevScores[student],
                            [exam.name]: newScore,
                          },
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateScore(student, exam.name, e.target.value);
                        }
                      }}
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add New Exam</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Exam Name"
            fullWidth
            value={newExam.name}
            onChange={(e) => setNewExam({ ...newExam, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Maximum Marks"
            type="number"
            fullWidth
            value={newExam.maxMarks}
            onChange={(e) =>
              setNewExam({ ...newExam, maxMarks: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddExam}
            variant="contained"
            sx={{ backgroundColor: "#FF9800" }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Exams;
